import {
    AssignmentOrigin,
    AssignmentState,
    BalanceCheck,
    DocumentAccountRef,
    DocumentFiling,
    DocumentProcessingStep,
    DocumentReadingState,
    DocumentSource,
    DocumentUpstreamState,
    DocumentValidity,
    ProposalCertainty,
    ProposalSource,
    SharepointSkipReason,
    SyncRunStatus,
    SyncStartRefusal,
} from '../types/closing-documents.types';
import { DocumentPreviewKind } from '../components/document-preview/document-preview.types';
import {
    DocumentInterpretationReason,
    DocumentProposalRefusal,
    DocumentReadingQuality,
} from '../components/document-detail/document-detail.types';

//REV-21376 — the WIRE shapes of the ABCP document API (`aumico-be`, ADR-0009 Technical Design).
//
// These reuse the pure views' vocabulary from `types/closing-documents.types.ts` on purpose: the
// backend already answers in that spelling (its response mapper is the ONE place that knows both
// `PROJECT_FILE` and `projectFile`), so the container's mapping into `DocumentRow` is a rename and
// never a translation. Redeclaring the unions here would create a second place for them to drift.

/** One row as `GET /api/projects/:projectId/documents` returns it. */
export interface IClosingDocumentListItem {
    id: string;
    filename: string;
    /** Booking owner when assigned, the PROPOSED account when proposed, `null` otherwise. */
    account: DocumentAccountRef | null;
    filing: DocumentFiling;
    assignment: AssignmentState;
    /**
     * `null`/absent (#57) is LEGACY DATA — a document assigned before this field existed — and is
     * deliberately NOT `'manual'`: nobody knows who filed it.
     */
    assignedBy?: AssignmentOrigin | null;
    /** 0–100 or `null` → the hub renders `—`. Normal until REV-21358 lands. */
    confidence: number | null;
    /** ISO-8601. Formatted by the view, never re-timed. */
    addedAt: string;
    source: DocumentSource;
    notRelevant: boolean;
    validity: DocumentValidity;
    /**
     * REV-21964 · C1–C4 — where the upstream file stands. Absent on a manual upload and on any row
     * written before the field existed; both mean `'present'`, which is why the view defaults to it
     * rather than treating absence as a problem.
     */
    upstreamState?: DocumentUpstreamState;
    /** When that state last CHANGED — not when it was last checked. ISO-8601. */
    upstreamStateChangedAt?: string | null;
    /**
     * C4 — the name SharePoint reports TODAY, when it differs from the one the file was stored
     * under. The hub prefers this over `filename`: a rename should show up as a new name, and the
     * file store legitimately keeps recording what was uploaded.
     */
    upstreamName?: string | null;
    /** ID2 — other upstream names for the same bytes. Shown as aliases, never as extra rows. */
    aliasNames?: readonly string[];
    /**
     * REV-22010 — has the AI read the file? Absent = read (the resting value). Derived by the backend
     * from the file row it already loads for the filename, so the list stays one query.
     */
    readingState?: DocumentReadingState;
    /**
     * REV-22302 — the Belegart the classifier recognised (`extraction.kind`, a kernel `DocumentKind`
     * identifier). The hub shows its label in the Belegtyp column instead of the self-reported
     * confidence. Absent = not recognised (yet) → `—`.
     */
    kind?: string | null;
    /**
     * REV-22302 — the classifier's raw refusal code (`extraction.refusalCode`), sent only while it is
     * the last word about the document (no proposal state, nothing filed — the gate `toProposalRefusal`
     * applies to the detail's coarser `proposalRefusal`). The hub says "Kein Vorschlag" and names the
     * reason per code; an unknown code gets a generic sentence, never the code.
     */
    proposalRefusalCode?: string | null;
    /**
     * REV-22302 — a human rejected the live proposal (`extraction.state === 'REJECTED'`). Without it a
     * rejected row and a never-proposed row are the same `assignment: 'open'`, and the hub could not
     * tell "the AI is still looking" from "the AI's answer was turned down".
     */
    proposalRejected?: boolean;
    /** REV-22302 — the other strand's account when content and balance disagree. */
    alternativeAccountId?: string;
    proposalSource?: ProposalSource;
    proposalCertainty?: ProposalCertainty;
    balanceCheck?: BalanceCheck;
    processingStep?: DocumentProcessingStep;
    /** Set when the document's balance is dated other than the closing's balance-sheet date (ISO dates). */
    dateWarning?: DocumentDateWarning | null;
}

export interface DocumentDateWarning {
    balanceDate: string;
    balanceSheetDate: string;
}

/**
 * REV-21869 #164 — what the AI read from the document, exactly as `ClosingDocumentDetail.
 * interpretation` sends it (be `closing-documents-service.interface.ts`). A POSITIVE LIST: no cell
 * text, no raw model answer, no rule — amounts stay in MINOR UNITS, converted exactly once by the FE
 * mapper (`closing-document.selectors.ts`), never here and never in the component.
 */
export interface IClosingDocumentInterpretation {
    /** One value for most kinds; market value and cost for a Depotauszug. */
    values?: readonly {
        label: 'BALANCE' | 'MARKET_VALUE' | 'COST';
        amountMinor: number;
        currency: string;
        date?: string;
        quote?: string;
    }[];
    /** The document's own date, information only. */
    documentDate?: string;
    /**
     * REV-22010 — `disabled` has a producer now (the org switched the normalisation off, or its budget
     * is spent), and the empty state MAY carry a `reason` from a CLOSED list — never a raw code, text
     * or figure (product decision, REV-22010). Absent `reason` = none known.
     */
    notInterpreted?: { state: 'interpreting' | 'noValue' | 'none' | 'disabled'; reason?: DocumentInterpretationReason };
}

/** The single-document read adds the preview. */
export interface IClosingDocumentDetail extends IClosingDocumentListItem {
    previewKind: DocumentPreviewKind;
    /** `null` until the scan releases the file — a placeholder state, not an error. */
    previewUrl: string | null;
    interpretation?: IClosingDocumentInterpretation;
    /** REV-22010 — the stored read's integrity verdict. Absent = not read. */
    readingQuality?: DocumentReadingQuality;
    /**
     * REV-22010 — why the classifier proposed no account, from a closed list. The backend sends it only
     * while the document is unfiled and carries no proposal state at all.
     */
    proposalRefusal?: DocumentProposalRefusal;
}

/**
 * The three pill counts. They come FROM THE SERVER and are never derived from `documents.length`:
 * the pills partition the whole closing while the client holds one filtered page, so a locally
 * computed count would be wrong in a way that looks plausible (ADR-0009 §9a).
 */
export interface IDocumentCounts {
    inbox: number;
    assigned: number;
    notRelevant: number;
}

export interface IClosingDocumentListResponse {
    documents: IClosingDocumentListItem[];
    counts: IDocumentCounts;
}

/** One rejected file, fed back into the upload modal's `[failures]`. */
/**
 * The account plan the pickers list, from `GET /projects/:id/accounts/plan`.
 *
 * The STORED shape, not the displayed one. `accountGroup` is the balance-sheet or income-statement
 * position as the backend's `AccountGroupKey` enum — a key, never a German label: the position names
 * live in the translation files (`abcp.accountSurface.position.*`), and a second catalogue on the
 * server would be one more thing to keep in step. `ClosingDocumentProvider.getAccounts$()` is where
 * the key becomes text, which is also where the pure views' `AccountOption` is produced.
 *
 * ⚠️ It used to be byte-identical to `AccountOption` and fed in without a map. That held only while
 * the plan was a fixture that carried its own German `klasse` and `group` strings; the real plan
 * carries a position binding, and an unbound account carries `null`.
 */
export interface IClosingAccountOption {
    id: string;
    number: string;
    name: string;
    /** `null` while the account is not bound to a position — an ordinary state, not an error. */
    accountGroup: string | null;
}

/**
 * The wire shape of one plan entry, exactly as the route sends it.
 *
 * Its own type because the field NAMES differ: the backend answers `accountId` / `accountNumber`,
 * which is what its own account surface calls them. Renaming happens once, in the service.
 */
export interface IAccountPlanEntryResponse {
    accountId: string;
    accountNumber: string;
    name: string;
    accountGroup: string | null;
}

export interface IClosingAccountsResponse {
    accounts: IAccountPlanEntryResponse[];
}

/**
 * The assignment write. `projectFile` is a first-class TARGET, never "no account" — the two are
 * different operations (ADR-0009 §9a) and un-filing is a DELETE, not this with a null id.
 */
export interface ISetAssignmentRequest {
    kind: 'account' | 'projectFile';
    accountId?: string;
    /** Absent means "leave it as it is" — NOT "reset to closing". */
    validity?: 'closing' | 'longTerm';
}

export interface IUploadFailure {
    filename: string;
    reason: string;
}

/** Where the last upload stands: still in flight, and what it refused once it settled. */
export interface IUploadState {
    pending: boolean;
    failures: IUploadFailure[];
}

/** One file in a bound SharePoint folder, as `GET /sharepoint-files` reports it (REV-21376 slice 2). */
export interface ISharepointFileItem {
    driveId: string;
    itemId: string;
    filename: string;
    size: number | null;
    /** Already in this closing at this exact version. */
    alreadyImported: boolean;
    isPdf: boolean;
}

export interface ISharepointListResponse {
    files: ISharepointFileItem[];
    skipped: number;
}

/**
 * REV-21964 promoted the three sync unions into `types/closing-documents.types.ts` — for the reason
 * stated at the top of this file: the hub renders them, the backend answers in this spelling, and a
 * second declaration is a second place for the two to drift. Re-exported so every import path that
 * reached for them HERE keeps working.
 *
 * `SharepointSkipReason` in particular had already drifted once (`tooLarge` existed on the backend
 * for two months and not here), which is exactly what one declaration prevents.
 */
export {
    DocumentUpstreamState,
    SharepointSkipReason,
    SyncRunStatus,
    SyncStartRefusal,
} from '../types/closing-documents.types';

export interface ISharepointSkip {
    filename: string;
    reason: SharepointSkipReason;
}

/**
 * The result of a synchronous sync. `created: 0` with a non-empty `skipped` is the NORMAL outcome of
 * syncing twice — so the UI must be able to say "nothing new" rather than "nothing happened".
 *
 * ⚠️ SUPERSEDED by the run report (REV-21964 · R3/U1). The button no longer waits for a result — it
 * gets a run id back and the work continues in the background, because five hundred files do not fit
 * in one request. Kept because the synchronous route still serves the folder-binding trigger.
 */
export interface ISharepointFetchResponse {
    created: number;
    skipped: ISharepointSkip[];
}

/**
 * What `POST …/sharepoint-files/sync` answers (R3).
 *
 * All three refusals are ANSWERS, not errors — `alreadyRunning` even carries the id of the run
 * already in flight, so the UI can simply follow that one. None should be rendered as a failure: a
 * failure invites the user to press the button again, which is the wrong advice in all three cases.
 */
export interface ISyncStartResult {
    runId: string | null;
    accepted: boolean;
    refusal?: SyncStartRefusal;
}

/** One bound folder's progress inside a run — the unit R1 reports failures at. */
export interface ISyncRunBinding {
    bindingId: string;
    /** The path the user recognises; what a per-folder error must name. */
    folderPath: string;
    state: 'pending' | 'done' | 'failed';
    /** A message, never a raw error: it is rendered to a trustee. */
    error?: string | null;
}

export interface ISyncRunCounts {
    discovered: number;
    created: number;
    skipped: number;
    /** Folders that could not be read at all (R1). A FILE that failed is a skip, not this. */
    failedFolders: number;
}

/**
 * One run's report (U1) — counts and per-file reasons, read back from the server.
 *
 * This is what makes the report survive a reload. REV-21376 returned the same information in the
 * fetch response and kept none of it, so "why is my document not there?" had an answer for exactly
 * as long as the tab stayed open.
 */
export interface ISyncRun {
    id: string;
    status: SyncRunStatus;
    startedAt: string;
    finishedAt?: string | null;
    counts: ISyncRunCounts;
    skips: ISharepointSkip[];
    /**
     * `true` when more files were skipped than the run stores.
     *
     * ⚠️ Must be SHOWN. The count stays exact while the list stops, and a list that merely ended
     * would tell the user that everything else succeeded (ADR-0009 §12).
     */
    skipsTruncated: boolean;
    bindings: ISyncRunBinding[];
}
